if (!customElements.get('viia-stock-notice')) {
  customElements.define('viia-stock-notice', class extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      if (!this.form) return;
      this.variants = JSON.parse(this.querySelector('[data-viia-stock-variants]').textContent);
      this.update = () => requestAnimationFrame(() => {
        const variant = this.variants.find(item => String(item.id) === this.form.querySelector('[name="id"]')?.value);
        this.hidden = !variant?.available;
      });
      this.form.addEventListener('change', this.update);
      document.addEventListener('variant:changed', this.update);
      this.observer = new MutationObserver(this.update);
      const submit = this.form.querySelector('[type="submit"]');
      if (submit) this.observer.observe(submit, {attributes:true, attributeFilter:['disabled']});
      this.update();
    }
    disconnectedCallback() {
      this.form?.removeEventListener('change', this.update);
      document.removeEventListener('variant:changed', this.update);
      this.observer?.disconnect();
    }
  });
}
if (!customElements.get('viia-payment-sync')) {
  customElements.define('viia-payment-sync', class extends HTMLElement {
    connectedCallback() {
      const form = this.closest('form');
      const quantity = form?.querySelector('[data-qty-input]');
      const input = form?.querySelector('[data-viia-bundle-quantity]');
      if (!quantity || !input) return;
      const sync = () => { input.value = String(parseInt(quantity.value || quantity.textContent, 10) || 1); };
      this.observer = new MutationObserver(sync);
      this.observer.observe(quantity, {subtree:true, childList:true, characterData:true});
      sync();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  });
}

if (!customElements.get('viia-supply')) {
  customElements.define('viia-supply', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = this.closest('form');
      if (!this.form) return;
      this.ready = true;
      this.radios = Array.from(this.querySelectorAll('[data-viia-supply-radio]'));
      this.onetime = this.querySelector('[data-viia-supply-onetime]');
      this.quantity = this.querySelector('[data-viia-supply-quantity]');
      try {
        this.sizes = JSON.parse(this.querySelector('[data-viia-supply-sizes]')?.textContent || '[]');
      } catch (error) {
        this.sizes = [];
      }
      this.sizeInputs = Array.from(this.querySelectorAll('[data-viia-supply-size]'));
      this.once = this.form.querySelector('[data-viia-buy-once]');
      this.onChange = (event) => {
        const size = event.target.closest('[data-viia-supply-size]');
        if (size) {
          this.setSize(Number(size.value));
          return;
        }
        const radio = event.target.closest('[data-viia-supply-radio]');
        if (radio) this.select(radio);
      };
      // The theme's variant logic rewrites the price after each change.
      this.onVariantChanged = () => window.setTimeout(() => this.render(), 0);
      this.onOnce = () => this.buyOnce();
      // A shopper who picks the same card three times in a row wants it:
      // the third pick adds it to the cart. A label click reaches the radio
      // as one click, so each pick counts once.
      this.onPick = (event) => {
        const radio = event.target.closest('[data-viia-supply-radio]');
        if (!radio || radio.disabled) return;
        this.picks = radio === this.lastPick ? this.picks + 1 : 1;
        this.lastPick = radio;
        if (this.picks < 3) return;
        this.picks = 0;
        this.addToCart();
      };
      this.picks = 0;
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onPick);
      document.addEventListener('variant:changed', this.onVariantChanged);
      this.once?.addEventListener('click', this.onOnce);
      const start = this.radios.find((radio) => radio.checked && !radio.disabled) || this.radios.find((radio) => !radio.disabled) || this.radios[0];
      this.select(start);
    }

    // A gummy count updates both cards: the first card is that count every
    // 30 days, the second card five times it every 90 days.
    setSize(index) {
      const size = this.sizes[index];
      if (!size) return;
      this.picks = 0;
      this.radios.forEach((radio, position) => {
        const card = size.cards[position];
        const box = radio.closest('.viia-supply__card');
        if (!card || !box) return;
        radio.value = card.plan;
        radio.dataset.variantId = card.variantId;
        radio.dataset.option = card.option;
        radio.dataset.quantity = card.quantity;
        radio.dataset.available = String(card.available);
        radio.dataset.price = card.price;
        radio.dataset.compare = card.compare;
        radio.dataset.oncePrice = card.once;
        radio.dataset.gummies = card.gummies;
        radio.disabled = !card.available;
        box.classList.toggle('is-unavailable', !card.available);
        const text = (selector, value) => {
          const element = box.querySelector(selector);
          if (!element) return;
          element.textContent = value;
          element.hidden = !value;
        };
        text('[data-viia-supply-title]', card.title);
        text('[data-viia-supply-save]', card.save);
        text('[data-viia-supply-price]', card.price);
        text('[data-viia-supply-compare]', card.compare);
        text('[data-viia-supply-meta]', card.meta);
        text('[data-viia-supply-per]', card.perGummy);
        const soldout = box.querySelector('[data-viia-supply-soldout]');
        if (soldout) soldout.hidden = card.available;
      });
      const radio = this.current && !this.current.disabled ? this.current : this.radios.find((item) => !item.disabled) || this.current;
      this.select(radio);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onPick);
      document.removeEventListener('variant:changed', this.onVariantChanged);
      this.once?.removeEventListener('click', this.onOnce);
      this.ready = false;
    }

    select(radio) {
      if (!radio) return;
      radio.checked = true;
      this.current = radio;
      this.radios.forEach((item) => item.closest('.viia-supply__card')?.classList.toggle('is-selected', item === radio));

      // Drive the hidden pack picker so the theme updates the variant, button and media.
      const source = Array.from(this.form.querySelectorAll('.viia-supply-source input[type="radio"]')).find(
        (input) => input.value === radio.dataset.option
      );
      if (source && !source.checked) {
        source.checked = true;
        source.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.quantity) this.quantity.value = radio.dataset.quantity || '1';
      const idInput = this.form.querySelector('input[name="id"]');
      if (idInput && idInput.value !== radio.dataset.variantId) {
        idInput.value = radio.dataset.variantId;
        this.form.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.render();
    }

    render() {
      const radio = this.current;
      if (!radio || !radio.checked) return;
      const priceEl = this.closest('[data-product-container]')?.querySelector('.price-wrapper [data-currentPrice]');
      if (priceEl) {
        priceEl.innerHTML = radio.dataset.price;
        const compareEl = priceEl.closest('.price-wrapper')?.querySelector('[data-comparePrice]');
        if (compareEl) {
          compareEl.innerHTML = radio.dataset.compare || '';
          compareEl.classList.toggle('d-none', !radio.dataset.compare);
        }
      }
      if (this.once && !this.once.classList.contains('loading')) {
        this.once.disabled = radio.dataset.available !== 'true';
        const onceText = this.once.querySelector('[data-viia-buy-once-text]');
        if (onceText && this.dataset.onceLabel) onceText.textContent = this.dataset.onceLabel.replace('%PRICE%', radio.dataset.oncePrice);
      }
    }

    // Runs the theme's add to cart button, so the state rules, the cart
    // drawer and the selected plan apply as for a click on it.
    addToCart() {
      const button = this.form.querySelector('.pdp-btn .submit_btn[name="add"]');
      if (!button || button.disabled || button.classList.contains('loading')) return;
      if (button.getClientRects().length === 0) return;
      button.click();
    }

    // The theme's product form adds the item. A one-time radio is checked
    // for this one submit, then the selected plan is restored.
    buyOnce() {
      if (!this.onetime || !this.once || this.once.disabled) return;
      const plan = this.current;
      let started = false;
      let done = false;
      const restore = () => {
        if (done) return;
        done = true;
        observer.disconnect();
        window.clearTimeout(timer);
        if (plan) plan.checked = true;
      };
      const observer = new MutationObserver(() => {
        const busy = this.once.classList.contains('loading') || this.once.hasAttribute('disabled');
        if (busy) started = true;
        else if (started) restore();
      });
      observer.observe(this.once, { attributes: true, attributeFilter: ['class', 'disabled'] });
      const timer = window.setTimeout(restore, 15000);
      this.onetime.checked = true;
      window.setTimeout(() => {
        if (!started && !this.once.classList.contains('loading')) restore();
      }, 0);
    }
  });
}
